import React, { useCallback, useEffect, useRef, useState } from "react";

import { Storage } from "aws-amplify";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Portal,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";

import { useNotify } from "react-admin";

import { SignatureDialog } from "../../../managerApp/resources/outboundShipments/SignatureDialog";
import { performUpdateInboundMini } from "../../../graphql/inbound/inboundCRUD";
import { CheckIcon, ClearIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import { generateSignatureKey } from "../../../utils/fileUtils";
import { useInboundContext } from "./context";

// Both signatures the inbound carries, in the order the yard collects them.
const SIGNATURE_FIELDS = [
  {
    field: "yardSignatureKey",
    label: "Yard Signature",
    shortLabel: "Yard Sig",
  },
  {
    field: "carrierSignatureKey",
    label: "Carrier Signature",
    shortLabel: "Carrier Sig",
  },
];

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: `${theme.zIndex.modal + 1} !important`,
    color: "white",
  },
}));

const readBlobAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const SignaturesDialog = ({ open, onClose, disabled = false }) => {
  const classes = useStyles();
  const notify = useNotify();
  const { state, dispatch } = useInboundContext();
  const { currentShipment } = state;
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const [activeField, setActiveField] = useState(null);
  const [signatureImages, setSignatureImages] = useState({});
  const [invalidSignatureMessage, setInvalidSignatureMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const signatureRef = useRef(null);

  const loadSignature = useCallback(
    async (key, field) => {
      if (!key) return;
      try {
        const data = await Storage.get(key, { download: true });
        const dataUrl = await readBlobAsDataUrl(data.Body);
        setSignatureImages((prev) => ({ ...prev, [field]: dataUrl }));
      } catch (err) {
        logError("SignaturesDialog:loadSignature", err, notify);
      }
    },
    [notify],
  );

  useEffect(() => {
    loadSignature(currentShipment?.yardSignatureKey, "yardSignatureKey");
  }, [currentShipment?.yardSignatureKey, loadSignature]);

  useEffect(() => {
    loadSignature(currentShipment?.carrierSignatureKey, "carrierSignatureKey");
  }, [currentShipment?.carrierSignatureKey, loadSignature]);

  const openSignaturePad = (field) => {
    setInvalidSignatureMessage("");
    setActiveField(field);
  };

  const closeSignaturePad = () => {
    signatureRef.current = null;
    setActiveField(null);
  };

  const addSignature = async ({ signatureBinaryImage }) => {
    if (!activeField) return;
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setInvalidSignatureMessage("Invalid signature");
      return;
    }
    const field = activeField;
    try {
      setSaving(true);
      const key = generateSignatureKey(window.appUser?.reloadYardId);
      const stored = await Storage.put(key, signatureBinaryImage, {
        level: "public",
        contentType: "image/png",
      });
      if (!stored?.key) return;

      const signedBy = window.appUser?.fullName;
      // Narrow write: the speed dial only ever touches the signature columns, so
      // the header the crew is editing on the card is not round-tripped here.
      await performUpdateInboundMini({
        id: currentShipment.id,
        [field]: key,
        signedBy,
      });
      dispatch({
        type: "UPDATE_CURRENT_SHIPMENT",
        payload: { ...currentShipment, [field]: key, signedBy },
      });
      await loadSignature(key, field);
      closeSignaturePad();
    } catch (err) {
      logError("SignaturesDialog:addSignature", err, notify);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Portal>
        <Backdrop open={saving} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Portal>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <Box
          component="span"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          m={2}
        >
          <Typography variant="h6">Signatures</Typography>
          <CancelOutlinedIcon fontSize="large" onClick={onClose} />
        </Box>
        <DialogContent>
          <Grid container spacing={2}>
            {SIGNATURE_FIELDS.map(({ field, label, shortLabel }) => (
              <Grid item xs={12} key={field}>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  disabled={disabled || saving}
                  onClick={() => openSignaturePad(field)}
                  endIcon={
                    currentShipment?.[field] ? (
                      <CheckIcon fontSize="large" />
                    ) : (
                      <ClearIcon fontSize="large" />
                    )
                  }
                >
                  {isSmall ? shortLabel : label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
      {activeField && (
        <SignatureDialog
          open
          handleClose={closeSignaturePad}
          saving={saving}
          signatureImg={signatureImages[activeField]}
          signatureRef={signatureRef}
          addSignature={addSignature}
          invalidSignatureMessage={invalidSignatureMessage}
          clearSignature={() => setInvalidSignatureMessage("")}
        />
      )}
    </>
  );
};
